import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IBoardColumnProps } from 'src/types/boards-lists';
import { SortableCard } from './SortableCard';

export const BoardColumn = ({
  column,
  isSmall,
  onAddCard,
  handleOpenEditBoard,
  handleOpenDeleteBoard,
}: IBoardColumnProps) => {
  const { setNodeRef } = useDroppable({ id: column.id });
  const navigate = useNavigate();

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={3}
      zeroMinWidth
      sx={{ display: 'flex' }}
      minWidth={isSmall ? '100%' : '450px'}
    >
      <Paper
        ref={setNodeRef}
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          minHeight: 150,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: (theme) => theme.palette.background.paper,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          '&:hover': {
            background: (theme) => theme.palette.action.hover,
            boxShadow: (theme) => `0 4px 10px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.25)'}`,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            transform: 'translateY(-2px)',
          },
        }}
        onClick={() => {
          navigate(`/boards/${column.id}`, {
            state: {
              board_name: column.title,
              board_subtitle: column.subtitle,
              board_tags: column.tags,
              board_description: column.description,
            },
          });
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          noWrap
          sx={{ color: (theme) => theme.palette.primary.main, mb: 0.5 }}
          title={column.title}
        >
          {column.title}
        </Typography>
        <Typography
          noWrap
          variant="h6"
          fontWeight={700}
          sx={{ color: (theme) => theme.palette.text.primary, mb: 0.5 }}
          title={column.subtitle}
        >
          {column.subtitle || 'No subtitle'}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: (theme) => theme.palette.text.secondary, mb: 2 }}
          title={column.description}
        >
          {column.description || 'No description'}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap={1}
          sx={{ mb: 2 }}
          flexWrap={'wrap'}
        >
          {column.tags &&
            column.tags.map((item, index) => {
              return (
                <Box
                  key={index}
                  sx={{
                    background: (theme) => theme.palette.action.selected,
                    borderRadius: 1,
                    padding: '6px 12px',
                    marginRight: 1,
                    marginBottom: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: (theme) => theme.palette.text.secondary, fontWeight: 500 }}
                  >
                    {item}
                  </Typography>
                </Box>
              );
            })}
        </Box>

        <Divider sx={{ borderColor: (theme) => theme.palette.divider, mb: 2 }} />

        <SortableContext
          items={column.cards.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box flex={1} sx={{ overflowY: 'auto', mb: 2 }}>
            {column.cards.map((card) => (
              <SortableCard key={card.id} card={card} />
            ))}
          </Box>
        </SortableContext>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 'auto', pt: 1 }}
        >
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.text.secondary, fontWeight: 500 }}
          >
            {`${1} list${1 !== 1 ? 's' : ''} · ${column.cards.length} card${
              column.cards.length !== 1 ? 's' : ''
            }`}
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditBoard(column);
              }}
              sx={{ color: (theme) => theme.palette.text.secondary, mr: 1 }}
              title="Edit board"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: (theme) => theme.palette.text.secondary }}
              title="Delete board"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteBoard(column);
              }}
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12Zm3.46-8.12a1 1 0 1 1 2 0v6.24a1 1 0 1 1-2 0V10.88Zm4 0a1 1 0 1 1 2 0v6.24a1 1 0 1 1-2 0V10.88ZM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4Z"
                />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};
